import React, { useState, useRef, useEffect } from 'react';
import { BrowserMultiFormatReader, DecodeHintType } from '@zxing/library';
import { Box, Typography, Modal, MenuItem, Select, FormControl, InputLabel } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const BarcodeScanner = ({ open, onClose, onScan }) => {
    const [scanning, setScanning] = useState(false);
    const [error, setError] = useState(null);
    const [devices, setDevices] = useState([]);
    const [selectedDevice, setSelectedDevice] = useState('');
    const videoRef = useRef(null);
    const codeReaderRef = useRef(null);
    const beepAudioRef = useRef(null);

    useEffect(() => {
        const hints = new Map();
        const formats = ['CODE_128', 'CODE_39', 'CODE_93', 'EAN_8', 'EAN_13', 'UPC_A', 'UPC_E', 'ITF', 'CODABAR', 'RSS_14', 'RSS_EXPANDED', 'QR_CODE', 'DATA_MATRIX', 'AZTEC', 'PDF_417', 'MAXICODE'];
        hints.set(DecodeHintType.POSSIBLE_FORMATS, formats);
        hints.set(DecodeHintType.TRY_HARDER, true);
        codeReaderRef.current = new BrowserMultiFormatReader(hints);
        const audioContext = new (window.AudioContext || window.webkitAudioContext)();
        beepAudioRef.current = audioContext;
        navigator.mediaDevices.enumerateDevices().then(ds => {
            const videoInputs = ds.filter(d => d.kind === 'videoinput').reverse();
            setDevices(videoInputs);
            if (videoInputs.length > 0) setSelectedDevice(videoInputs[0].deviceId);
        });
        return () => stopScanning();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (open) { setTimeout(() => startScanning(), 100); } 
        else { stopScanning(); }
        // eslint-disable-next-line
    }, [open, selectedDevice]);

    const playRetailBeep = () => {
        if (!beepAudioRef.current) return;
        const ctx = beepAudioRef.current;
        const now = ctx.currentTime;
        const osc1 = ctx.createOscillator();
        const gain1 = ctx.createGain();
        osc1.connect(gain1); gain1.connect(ctx.destination);
        osc1.frequency.value = 2800; osc1.type = 'square';
        gain1.gain.setValueAtTime(0, now); gain1.gain.linearRampToValueAtTime(0.3, now + 0.005); gain1.gain.exponentialRampToValueAtTime(0.01, now + 0.05);
        osc1.start(now); osc1.stop(now + 0.05);
    };

    const startScanning = async () => {
        if (!selectedDevice) return;
        setScanning(true); setError(null);
        try {
            const constraints = { video: { deviceId: { exact: selectedDevice }, width: { ideal: 1920 }, height: { ideal: 1080 } } };
            await codeReaderRef.current.decodeFromConstraints(constraints, videoRef.current, (result, err) => {
                if (result) {
                    playRetailBeep();
                    onScan(result.getText());
                    // setTimeout(() => { stopScanning(); }, 400);
                }
            });
        } catch (err) {
            setError(err.message);
            setScanning(false);
        }
    };

    const stopScanning = () => {
        if (codeReaderRef.current) codeReaderRef.current.reset();
        setScanning(false);
    };

    return (
        <Modal open={open} onClose={onClose} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Box sx={{ p: 3, bgcolor: "#fff", width: "100%", maxWidth: "600px", borderRadius: 4, display: "flex", flexDirection: "column", gap: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 18 }}>Scan Barcode</Typography>
                    <CloseIcon onClick={onClose} sx={{ cursor: "pointer", color: "text.secondary" }} />
                </Box>
                
                <FormControl fullWidth size="small">
                    <InputLabel>Select Camera</InputLabel>
                    <Select value={selectedDevice} label="Select Camera" onChange={(e) => setSelectedDevice(e.target.value)}>
                        {devices.map((device, i) => <MenuItem key={device.deviceId} value={device.deviceId}>{device.label || `Camera ${i + 1}`}</MenuItem>)}
                    </Select>
                </FormControl>

                <Box sx={{ position: 'relative', bgcolor: '#000', borderRadius: 2, overflow: 'hidden', minHeight: '300px' }}>
                    <video ref={videoRef} style={{ width: '100%', height: 'auto', maxHeight: '400px', display: 'block' }} />
                    {scanning && <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '80%', height: '200px', border: '3px solid #4CAF50', borderRadius: 2, pointerEvents: 'none', boxShadow: '0 0 0 9999px rgba(0,0,0,0.3)' }} />}
                </Box>
                {error && <Box sx={{ p: 1.5, bgcolor: '#ffebee', color: '#c62828', borderRadius: 1, fontSize: 14 }}>{error}</Box>}
            </Box>
        </Modal>
    );
};

export default BarcodeScanner;